/**
 * Agency Billing API Client
 * Handles all API calls related to agency seat billing and agent subscriptions
 */

import { rateGate } from '@/app/_module/utils/rateGate';
import { getSupabaseSession } from '../auth';
import { getApiUrl } from '../config';
import { endpoint } from '../endpoints';
import type {
  GetAgencySeatsResponse,
  AddSeatRequest,
  RemoveSeatRequest,
  ChangePlanRequest,
  TogglePaymentSourceRequest,
  ProrationPreviewResponse,
  BillingOperation,
} from '../../types/agency-billing.types';

/**
 * Get all agency seats with summary information
 */
export const getAgencySeats = async (
  agencyId: string
): Promise<GetAgencySeatsResponse> => {
  const session = await getSupabaseSession();
  if (!session?.access_token) {
    throw new Error('No authentication session found');
  }

  const response = await fetch(
    getApiUrl(`/agencies/${agencyId}/billing/seats`),
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch agency seats' }));
    throw new Error(error.message || 'Failed to fetch agency seats');
  }

  const result = await response.json();
  // Backend returns { statusCode, message, data: { seats, summary }, timestamp }
  // We need to return just { seats, summary }
  return result.data || result;
};

/**
 * Add a new agent seat to the agency billing
 */
export const addAgentSeat = async (
  request: AddSeatRequest
): Promise<BillingOperation> => {
  const session = await getSupabaseSession();
  if (!session?.access_token) {
    throw new Error('No authentication session found');
  }

  const response = await fetch(
    getApiUrl(`/agencies/${request.agencyId}/billing/seats`),
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: request.userId,
        planType: request.planType,
        paymentSource: request.paymentSource,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to add agent seat' }));
    throw new Error(error.message || 'Failed to add agent seat');
  }

  return response.json();
};

/**
 * Remove an agent seat from agency billing
 */
export const removeAgentSeat = async (
  request: RemoveSeatRequest
): Promise<BillingOperation> => {
  const session = await getSupabaseSession();
  if (!session?.access_token) {
    throw new Error('No authentication session found');
  }

  const response = await fetch(
    getApiUrl(`/agencies/${request.agencyId}/billing/seats/${request.userId}`),
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        reason: request.reason,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to remove agent seat' }));
    throw new Error(error.message || 'Failed to remove agent seat');
  }

  return response.json();
};

/**
 * Change an agent's subscription plan
 */
export const changeAgentPlan = async (
  request: ChangePlanRequest
): Promise<BillingOperation> => {
  const session = await getSupabaseSession();
  if (!session?.access_token) {
    throw new Error('No authentication session found');
  }

  const response = await fetch(
    getApiUrl(`/agencies/${request.agencyId}/billing/seats/${request.userId}/plan`),
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        newPlanType: request.newPlan,
      }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to change agent plan' }));
    throw new Error(error.message || 'Failed to change agent plan');
  }

  return response.json();
};

/**
 * Toggle payment source between agency and self-paid
 * Automatically detects current state and switches:
 * - Agency → Self: Removes agency seat, starts 7-day grace period
 * - Self → Agency: Cancels user subscription, adds agency seat
 */
export const togglePaymentSource = async (
  request: TogglePaymentSourceRequest
): Promise<BillingOperation> => {
  const session = await getSupabaseSession();
  if (!session?.access_token) {
    throw new Error('No authentication session found');
  }

  const response = await fetch(
    getApiUrl(`/agencies/${request.agencyId}/billing/seats/${request.userId}/payment-source`),
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to toggle payment source' }));
    throw new Error(error.message || 'Failed to toggle payment source');
  }

  return response.json();
};

/**
 * Get proration preview for plan changes
 */
export const getProrationPreview = async (
  agencyId: string,
  userId: string,
  newPlan: string
): Promise<ProrationPreviewResponse> => {
  const session = await getSupabaseSession();
  if (!session?.access_token) {
    throw new Error('No authentication session found');
  }

  const response = await fetch(
    getApiUrl(`/agencies/${agencyId}/billing/seats/${userId}/proration-preview?newPlan=${newPlan}`),
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to get proration preview' }));
    throw new Error(error.message || 'Failed to get proration preview');
  }

  return response.json();
};

/**
 * Get grace period information for a specific seat
 */
export const getGracePeriodInfo = async (
  agencyId: string,
  userId: string
): Promise<{ isInGracePeriod: boolean; daysRemaining: number; gracePeriodEnd?: string }> => {
  const session = await getSupabaseSession();
  if (!session?.access_token) {
    throw new Error('No authentication session found');
  }

  const response = await fetch(
    getApiUrl(`/agencies/${agencyId}/billing/seats/${userId}/grace-period`),
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to get grace period info' }));
    throw new Error(error.message || 'Failed to get grace period info');
  }

  return response.json();
};

/**
 * Promo code validation response
 */
export interface PromoCodeValidationResult {
  valid: boolean;
  error?: string;
  couponId?: string;
  promotionCodeId?: string;
  code?: string;
  discountDescription?: string;
  discountType?: 'percent' | 'fixed';
  discountAmount?: number;
  duration?: 'once' | 'repeating' | 'forever';
  durationInMonths?: number | null;
  name?: string | null;
}

/**
 * Validate a promo code for agency seat subscriptions
 */
export const validateAgencyPromoCode = async (
  agencyId: string,
  promoCode: string
): Promise<PromoCodeValidationResult> => {
  const session = await getSupabaseSession();
  if (!session?.access_token) {
    throw new Error('No authentication session found');
  }

  const response = await fetch(
    getApiUrl(`/agencies/${agencyId}/billing/validate-promo-code`),
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ promoCode }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to validate promo code' }));
    throw new Error(error.message || 'Failed to validate promo code');
  }

  const result = await response.json();
  return result.data || result;
};

/**
 * Apply a validated coupon to the agency subscription
 */
export const applyAgencyCoupon = async (
  agencyId: string,
  couponId: string
): Promise<{ discountApplied: boolean; subscriptionId?: string }> => {
  const session = await getSupabaseSession();
  if (!session?.access_token) {
    throw new Error('No authentication session found');
  }

  const response = await fetch(
    getApiUrl(`/agencies/${agencyId}/billing/apply-coupon`),
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ couponId }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to apply coupon' }));
    throw new Error(error.message || 'Failed to apply coupon');
  }

  const result = await response.json();
  return result.data || result;
};

// ────────────────────────────────────────────────────
// Enterprise Coverage API Functions
// ────────────────────────────────────────────────────

export interface EnterpriseCoverageStatus {
  isCovered: boolean;
  isOwner: boolean;
  isInEnterpriseHierarchy: boolean;
  enterpriseAgencyId: string | null;
  enterpriseAgencyName: string | null;
  ownerName: string | null;
  ownerEmail: string | null;
  coveredPlanType: string | null;
}

export interface HierarchyAgent {
  userId: string;
  name: string;
  email: string;
  agencyId: string;
  agencyName: string;
  role: string;
  accountType: string;
  accountStatus: string;
  isCovered: boolean;
  planType: string | null;
  seatStatus: string | null;
}

export interface EnterpriseSeatSummary {
  totalAgents: number;
  coveredAgents: number;
  selfPayAgents: number;
  maxCoveredSeats: number;
  seatsRemaining: number;
  coveredBasicSeats: number;
  coveredFullFledgedSeats: number;
  maxBasicSeats: number;
  maxFullFledgedSeats: number;
  basicSeatsRemaining: number;
  fullFledgedSeatsRemaining: number;
}

export interface EnterpriseAgencyOverview {
  agencyId: string;
  agencyName: string;
  isEnterprise: boolean;
  maxCoveredSeats: number;
  maxBasicSeats: number;
  maxFullFledgedSeats: number;
  activeCoveredSeats: number;
  coveredBasicSeats: number;
  coveredFullFledgedSeats: number;
  totalAgents: number;
  ownerName: string;
  ownerEmail: string;
  createdAt: string;
}

/**
 * Get the current user's enterprise coverage status
 */
// rateGate: several components share this queryFn (sidebar, billing tabs);
// one network call per 30 s however often they re-run (Safari 26 storm, 2026-09-17).
export const getEnterpriseCoverageStatus = (): Promise<EnterpriseCoverageStatus> =>
  rateGate('enterprise-coverage-status', 30_000, fetchEnterpriseCoverageStatus);

const fetchEnterpriseCoverageStatus = async (): Promise<EnterpriseCoverageStatus> => {
  const session = await getSupabaseSession();
  if (!session?.access_token) {
    throw new Error('No authentication session found');
  }

  const response = await fetch(
    getApiUrl(endpoint.agencySubscription.enterpriseStatus),
    {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch enterprise coverage status');
  }

  const result = await response.json();
  return result.data;
};

/**
 * Get all agents across the enterprise agency hierarchy (owner only)
 */
export const getHierarchyAgents = async (): Promise<HierarchyAgent[]> => {
  const session = await getSupabaseSession();
  if (!session?.access_token) {
    throw new Error('No authentication session found');
  }

  const response = await fetch(
    getApiUrl(endpoint.agencySubscription.hierarchyAgents),
    {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch hierarchy agents');
  }

  const result = await response.json();
  return result.data;
};

/**
 * Get enterprise seat usage summary (owner only)
 */
export const getEnterpriseSeatSummary = async (): Promise<EnterpriseSeatSummary> => {
  const session = await getSupabaseSession();
  if (!session?.access_token) {
    throw new Error('No authentication session found');
  }

  const response = await fetch(
    getApiUrl(endpoint.agencySubscription.seatSummary),
    {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch seat summary');
  }

  const result = await response.json();
  return result.data;
};

/**
 * Toggle enterprise coverage for a single agent (owner only)
 */
export const toggleEnterpriseCoverage = async (
  targetUserId: string,
  covered: boolean,
  planType: 'lite' | 'basic' | 'full_fledged' = 'basic',
): Promise<{ success: boolean; message: string }> => {
  const session = await getSupabaseSession();
  if (!session?.access_token) {
    throw new Error('No authentication session found');
  }

  const response = await fetch(
    getApiUrl(endpoint.agencySubscription.toggleCoverage),
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ targetUserId, covered, planType }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to toggle coverage' }));
    throw new Error(error.message || 'Failed to toggle coverage');
  }

  const result = await response.json();
  return result.data;
};

/**
 * Bulk toggle enterprise coverage for multiple agents (owner only)
 */
export const bulkToggleEnterpriseCoverage = async (
  userIds: string[],
  covered: boolean,
  planType: 'lite' | 'basic' | 'full_fledged' = 'basic',
): Promise<{ results: Array<{ userId: string; success: boolean; message: string }>; succeeded: number; failed: number }> => {
  const session = await getSupabaseSession();
  if (!session?.access_token) {
    throw new Error('No authentication session found');
  }

  const response = await fetch(
    getApiUrl(endpoint.agencySubscription.bulkToggleCoverage),
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userIds, covered, planType }),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to bulk toggle coverage' }));
    throw new Error(error.message || 'Failed to bulk toggle coverage');
  }

  const result = await response.json();
  return result.data;
};

// ────────────────────────────────────────────────────
// Admin Enterprise API Functions
// ────────────────────────────────────────────────────

/**
 * Get all enterprise agencies with seat usage stats (admin only)
 */
export const getAdminEnterpriseOverview = async (): Promise<EnterpriseAgencyOverview[]> => {
  const session = await getSupabaseSession();
  if (!session?.access_token) {
    throw new Error('No authentication session found');
  }

  const response = await fetch(
    getApiUrl(endpoint.adminAgencies.enterpriseOverview),
    {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch enterprise overview');
  }

  const result = await response.json();
  return result.data;
};

/**
 * Set seat limits for an enterprise agency (admin only)
 */
export const setEnterpriseSeatLimit = async (
  agencyId: string,
  limits: {
    maxCoveredSeats?: number;
    maxBasicSeats?: number;
    maxFullFledgedSeats?: number;
  },
): Promise<{ agencyId: string; agencyName: string; maxCoveredSeats: number; maxBasicSeats: number; maxFullFledgedSeats: number }> => {
  const session = await getSupabaseSession();
  if (!session?.access_token) {
    throw new Error('No authentication session found');
  }

  const response = await fetch(
    getApiUrl(endpoint.adminAgencies.setSeatLimit(agencyId)),
    {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(limits),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to set seat limits' }));
    throw new Error(error.message || 'Failed to set seat limits');
  }

  const result = await response.json();
  return result.data;
};
